import {
  Color4,
  Engine,
  HemisphericLight,
  Material,
  Mesh,
  Observer,
  Scene,
  Vector3,
} from 'babylonjs';
import AppContent from '@webcore/apps/app.content';
import { PlaneFinderContent } from './PlaneFinderContent';
import { CameraRig } from './CameraRig';
import { AnalysisSession } from '../solver/session';
import { RegionFx } from './fx/RegionFx';
import { RectController, type RectHandle } from './interaction/RectController';
import { TapPicker } from './interaction/TapPicker';
import { PlaneFinderStageMachine } from './interaction/stages';
import { Emitter } from './events';
import { worldToCss } from './projection';
import { swapToUnlit } from './capture/unlitSwap';
import { captureRegion } from './capture/TiledCapture';
import { computeIdealCaptureSize } from '../solver/capture.resolution';
import { rectCornersWorld } from './interaction/rect.math';
import type { TapResult } from '../solver/chain';
import type { PlaneBasis, RegionBounds2D, TexelDensityResult } from '../solver/types';
import type { RectState, PlaneFinderEvents, PlaneFinderStage } from '../core/types';

const MIN_REGION_TRIANGLES = 30;
// Culling: two clip planes parallel to the detected plane (front + back) discard
// geometry outside a slab around the surface. Each slider (0–100) is the kept
// distance in that direction, ramped from a safety floor up to this fraction of the
// rect's max dimension (so at 100 the slab is wide enough to keep ~everything).
const CULL_MAX_CLEARANCE_FRAC = 1.0;
// Cull dissolve duration (both in on framing, and back out on cancel).
const CULL_FADE_MS = 500;

/**
 * The framework-free orchestrator. Owns the Babylon app (lit viewport, retina,
 * AR features disabled via no-op overrides), the analysis worker session, the FX,
 * the camera rig, tap picking and the rect controller. Emits typed events the
 * React HUD subscribes to. The whole detect→frame→capture flow lives here.
 */
export class PlaneFinderApp extends AppContent {
  readonly events = new Emitter<PlaneFinderEvents>();

  private rig!: CameraRig;
  private readonly session = new AnalysisSession();
  private readonly fx = new RegionFx();
  private rectController!: RectController;
  private tapPicker!: TapPicker;
  private stages!: PlaneFinderStageMachine;
  private planeFinderContent?: PlaneFinderContent;

  private lastResult?: TapResult;
  private density?: TexelDensityResult;
  // Cull distances kept around the plane (0 = tight against the surface, cull the
  // most; 100 = far, cull the least), in front of and behind the surface.
  private cullFrontPct = 10;
  private cullBackPct = 10;
  private cullingActive = false; // clip planes engaged (during fly-to + framing)
  private cullFade = 1; // 0 = outside visible → 1 = outside culled; eased in during the fly-to
  private cullAnimObs: Observer<Scene> | null = null;
  private analysisDone = false; // worker precompute done? (gates tapping)
  private resizeObserver?: ResizeObserver;

  // Plane-detection sensitivity = the normal-angle tolerance (deg) growth accepts.
  // Higher → flatter-but-bumpier surfaces still grow into one region. 12° matches
  // growPlanarRegion's own default. `lastSeed` lets the slider re-grow the same tap.
  private sensitivityDeg = 12;
  private lastSeed: number | null = null;
  private sensitivityTimer: ReturnType<typeof setTimeout> | null = null;
  // Model-load serialization + staleness guard (see loadModel).
  private loadGen = 0;
  private loadChain: Promise<void> = Promise.resolve();
  private loadRequested = false;

  constructor(canvas: HTMLCanvasElement) {
    super(canvas);
    this.init(); // runs initialize() → engine/scene/camera/rootNode/share/callbacks
    this.setupInteraction();
  }

  // ---- Babylon lifecycle overrides (lit viewport, retina, AR off) ----

  protected override initEngine(): void {
    if (!this.renderCanvas) return;
    // Capture reads back a RenderTargetTexture (not the canvas), so no
    // preserveDrawingBuffer needed — keeps the per-frame cost off.
    this.engine = new Engine(this.renderCanvas, true, { stencil: false, preserveDrawingBuffer: false }, true);
    this.engine.resize(true);
    // AppBabylon only calls engine.resize() on the window 'resize' event. The canvas
    // can also change size with NO window resize — a sidebar/panel mounting, a
    // scrollbar appearing, HMR, or devtools opening. Without this the backing store
    // stays at its init size and the view renders too big / distorted. A
    // ResizeObserver keeps the drawing buffer matched to the displayed canvas.
    this.resizeObserver = new ResizeObserver(() => this.engine?.resize());
    this.resizeObserver.observe(this.renderCanvas);
  }

  protected override initScene(): void {
    this.scene = new Scene(this.engine!);
    this.scene.useRightHandedSystem = true;
    this.scene.clearColor = new Color4(0.082, 0.09, 0.11, 1);
    const light = new HemisphericLight('ejLight', new Vector3(0.3, 1, 0.4), this.scene);
    light.intensity = 1.0;
    this.scene.createDefaultEnvironment({ createSkybox: false, createGround: false });
  }

  protected override initCamera(): void {
    this.rig = new CameraRig(this.scene!, this.renderCanvas!);
    this.camera = this.rig.camera;
  }

  // No AR media recorder (avoids requesting camera/mic). Share is kept (base
  // initShare creates the web Share used by the review screen).
  protected override initMediaRecorder(): void {}

  // AR lifecycle is not used; resolve instead of the base's reject.
  public override async loadAsync(): Promise<void> {}
  public override async startXR(): Promise<void> {}
  public override async stopXR(): Promise<void> {}

  protected override render(): void {
    super.render(); // drives content.render(timeDelta)
    this.fx.update(this.timeDelta);
    if (this.stages.is('framing') && this.rig.hasUserMoved()) {
      this.cancelFraming();
    }
  }

  private setupInteraction(): void {
    this.stages = new PlaneFinderStageMachine((stage) => this.events.emit('stage', stage));
    this.rectController = new RectController(
      this.scene!,
      () => this.camera!,
      (rect) => this.onRectChange(rect),
    );
    this.tapPicker = new TapPicker(
      this.scene!,
      (tap) => void this.onTap(tap.mesh, tap.faceId, tap.worldPoint),
      () => this.stages.is('view', 'framing'),
    );
  }

  // ---- public API (driven by React) ----

  get stage(): PlaneFinderStage {
    return this.stages.stage;
  }

  /** Worker precompute finished (taps un-gated). Mirrors the 'analysis-ready' event
   *  so late subscribers (e.g. the React hook) can seed instead of assuming. */
  get analysisReady(): boolean {
    return this.analysisDone;
  }

  /** True once ANY model load has been requested. The harness's deferred demo-load
   *  checks this so it never queues behind — and then replaces — a user's drop. */
  get hasRequestedLoad(): boolean {
    return this.loadRequested;
  }

  loadModel(url: string, name = 'model.glb'): Promise<void> {
    // Serialize loads: two concurrent setContent calls interleave clearContent/init
    // (the base class disposes the old content BEFORE awaiting the new init), which
    // can dispose a still-initializing content and strand its meshes in the scene.
    // Realistic trigger: dropping a GLB while the lazy demo model is still loading.
    this.loadRequested = true;
    const run = this.loadChain.then(() => this.doLoadModel(url, name));
    this.loadChain = run.catch(() => {}); // keep the chain alive after a failure
    return run;
  }

  private async doLoadModel(url: string, name: string): Promise<void> {
    const gen = ++this.loadGen;
    this.fx.detach(); // also resets the cull
    this.lastResult = undefined;
    this.density = undefined;
    this.cullingActive = false;
    this.stopCullAnim();
    this.stages.set('empty');

    const content = new PlaneFinderContent(url);
    try {
      await this.setContent(content);
    } catch (e) {
      // console.error feeds Sentry's captureConsole integration (the standalone
      // entry enables it) — without it this handled failure never reaches telemetry.
      console.error('[plane-finder] model load failed:', e);
      this.events.emit('model-error', { message: (e as Error)?.message ?? 'Failed to load model' });
      return;
    }
    this.planeFinderContent = content;

    const bounds = content.bounds!;
    this.rig.frameModel(bounds.center, bounds.radius);
    this.fx.attach(content.planeFinderMeshes, bounds.diag);
    this.events.emit('model-loaded', {
      name,
      meshCount: content.planeFinderMeshes.length,
      triangleCount: content.triangleCount,
      bboxDiag: bounds.diag,
    });

    // The worker now pre-processes the model (weld/adjacency/normals). On dense
    // scans this takes seconds, so taps are gated until it finishes (see onTap).
    this.analysisDone = false;
    this.events.emit('analysis-ready', false);
    this.stages.set('view');

    if (content.snapshot) {
      // gen guard: if another model replaced this one while the worker was still
      // pre-processing, this (stale) resolve must not un-gate taps for the new model.
      this.session
        .setSnapshot(content.snapshot)
        .then(() => gen === this.loadGen && this.markAnalysisReady())
        .catch(() => gen === this.loadGen && this.markAnalysisReady()); // taps fall back to the no-result path
    } else {
      this.markAnalysisReady();
    }
  }

  private markAnalysisReady(): void {
    this.analysisDone = true;
    this.events.emit('analysis-ready', true);
  }

  /** Capture pixel cap = the GPU's max (render-)texture size, never above 8K (a
   *  single-RTT capture beyond that gets memory-heavy). Matches TiledCapture. */
  private captureMaxDim(): number {
    const caps = this.engine?.getCaps();
    if (!caps) return 8192;
    return Math.min(caps.maxTextureSize, caps.maxRenderTextureSize ?? caps.maxTextureSize, 8192);
  }

  /** Current cull distances 0–100 (so the panel can seed its sliders). The value is
   *  the kept distance from the plane in each direction: 0 = tight against the
   *  surface (cull the most), 100 = far (cull the least). */
  get cullFront(): number {
    return this.cullFrontPct;
  }
  get cullBack(): number {
    return this.cullBackPct;
  }

  /** Distance KEPT in front of the plane (toward the camera) before culling. */
  setCullFront(pct: number): void {
    this.cullFrontPct = pct;
    this.updateCull();
  }

  /** Distance KEPT behind the plane (away from the camera) before culling. */
  setCullBack(pct: number): void {
    this.cullBackPct = pct;
    this.updateCull();
  }

  /** Map a 0–100 slider to a kept distance from the plane: a linear ramp from the
   *  safety floor (so the plane's own relief is never clipped) up to a fraction of
   *  the rect's max dimension. 0 → floor (tightest), 100 → max (loosest). */
  private cullClearance(pct: number): number {
    const rect = this.rectController.rect;
    const maxRectDim = Math.max(rect.halfW, rect.halfH) * 2;
    const rms = this.lastResult?.region.plane.rms ?? 0;
    const floor = Math.max(2.5 * rms, 0.01 * maxRectDim);
    const max = CULL_MAX_CLEARANCE_FRAC * maxRectDim;
    return floor + (pct / 100) * Math.max(0, max - floor);
  }

  /** Drive the per-fragment cull (front + back slab) on the model's FX plugins. The
   *  slab distances are fixed at the slider targets; `cullFade` dither-dissolves the
   *  outside geometry (0 = fully visible, 1 = hard-culled). Runs on the live view AND
   *  the capture (the plugin is on the materials, so it survives the unlit pass). */
  private updateCull(): void {
    const basis = this.rectController.planeBasis;
    if (!this.cullingActive || !this.lastResult || !basis) {
      this.fx.setCull(null);
      return;
    }
    this.fx.setCull({
      origin: basis.origin,
      normal: basis.normal,
      front: this.cullClearance(this.cullFrontPct),
      back: this.cullClearance(this.cullBackPct),
      fade: this.cullFade,
    });
  }

  /** Ease cullFade to `target` over `durationMs` (ease-out), updating the cull each
   *  frame. Drives both the dissolve-in (on framing, synced to the fly-to) and the
   *  quick dissolve-out (on cancel — geometry fades back rather than popping). */
  private animateCullTo(target: number, durationMs: number, onDone?: () => void): void {
    const scene = this.scene;
    if (!scene) {
      this.cullFade = target;
      this.updateCull();
      onDone?.();
      return;
    }
    this.stopCullAnim();
    const from = this.cullFade;
    const start = performance.now();
    this.cullAnimObs = scene.onBeforeRenderObservable.add(() => {
      const t = Math.min(1, (performance.now() - start) / durationMs);
      const e = 1 - Math.pow(1 - t, 3); // ease-out cubic
      this.cullFade = from + (target - from) * e;
      this.updateCull();
      if (t >= 1) {
        this.cullFade = target;
        this.updateCull();
        this.stopCullAnim();
        onDone?.();
      }
    });
  }

  private stopCullAnim(): void {
    if (this.cullAnimObs) {
      this.scene?.onBeforeRenderObservable.remove(this.cullAnimObs);
      this.cullAnimObs = null;
    }
  }

  /** Fully release the cull (after the dissolve-out finishes). */
  private disengageCull(): void {
    this.cullingActive = false;
    this.cullFade = 1; // steady default for the next framing
    this.fx.setCull(null);
  }

  /** Current detection sensitivity in degrees (so the panel can seed its slider). */
  get sensitivity(): number {
    return this.sensitivityDeg;
  }

  /**
   * Set the plane-detection sensitivity (normal-angle tolerance, deg). While a
   * region is framed, this re-grows the same tap live (debounced) so the user
   * sees the region expand/contract as they drag. Takes effect on the next tap
   * otherwise.
   */
  setSensitivity(deg: number): void {
    this.sensitivityDeg = deg;
    if (!this.stages.is('framing') || this.lastSeed == null) return;
    if (this.sensitivityTimer) clearTimeout(this.sensitivityTimer);
    this.sensitivityTimer = setTimeout(() => {
      this.sensitivityTimer = null;
      void this.reanalyze();
    }, 50);
  }

  beginRectDrag(handle: RectHandle, cssX: number, cssY: number): void {
    this.rectController.beginDrag(handle, cssX, cssY);
  }

  dragRect(handle: RectHandle, cssX: number, cssY: number): void {
    if (this.stages.is('framing')) this.rectController.drag(handle, cssX, cssY);
  }

  /** Screen-space rect corners (BL,BR,TR,TL) for the SVG overlay, or null. */
  getOverlayCorners(): [number, number][] | null {
    if (!this.stages.is('framing') || !this.rectController.planeBasis) return null;
    const world = rectCornersWorld(this.rectController.planeBasis, this.rectController.rect);
    const css: [number, number][] = [];
    for (const c of world) {
      const p = worldToCss(this.scene!, this.camera!, new Vector3(c[0], c[1], c[2]));
      if (!p) return null;
      css.push([p.x, p.y]);
    }
    return css;
  }

  /** Subscribe to per-frame ticks (the overlay reprojects each frame). */
  addFrameObserver(cb: () => void): () => void {
    const obs: Observer<Scene> = this.scene!.onAfterRenderObservable.add(cb);
    return () => this.scene!.onAfterRenderObservable.remove(obs);
  }

  async capture(): Promise<void> {
    if (!this.planeFinderContent || !this.lastResult || !this.density || !this.stages.is('framing')) return;
    const basis = this.rectController.planeBasis;
    if (!basis) return;

    const rect = this.rectController.rect;
    const ideal = computeIdealCaptureSize(rect.halfW * 2, rect.halfH * 2, this.density, { maxDim: this.captureMaxDim() });

    this.stages.set('capturing');
    this.fx.captureMode(true);
    const restoreUnlit = swapToUnlit(uniqueMaterials(this.planeFinderContent.planeFinderMeshes));
    try {
      const result = await captureRegion(
        this.scene!,
        this.planeFinderContent.planeFinderMeshes,
        basis,
        rect,
        ideal.width,
        ideal.height,
        {
          // Match the live clip planes so the capture culls at the same depths (the
          // global clip planes refine front/back precisely within this slab).
          slabHalfDepth: Math.max(this.cullClearance(this.cullFrontPct), this.cullClearance(this.cullBackPct)),
          onProgress: (done, total) => this.events.emit('capture-progress', { done, total }),
        },
      );
      // The HUD downloads the PNG on this event; no review screen.
      this.events.emit('capture-complete', {
        ...result,
        texelsPerUnit: this.density.texelsPerUnit,
        clamped: ideal.clamped,
      });
    } catch (e) {
      console.error('[plane-finder] capture failed:', e);
      this.events.emit('capture-error', { message: (e as Error)?.message ?? 'Capture failed' });
    } finally {
      restoreUnlit();
      this.fx.captureMode(false);
      // Only return to framing if we're still the active flow — a model loaded
      // mid-capture has already moved the stage on (forcing 'framing' would strand
      // the new model in a framing UI with no plane).
      if (this.stages.is('capturing')) this.stages.set('framing');
    }
  }

  override dispose(): void {
    if (this.sensitivityTimer) clearTimeout(this.sensitivityTimer);
    this.resizeObserver?.disconnect();
    this.stopCullAnim();
    this.tapPicker?.dispose();
    this.session.dispose();
    this.fx.detach();
    this.events.clear();
    super.dispose(); // clears content (base class); does NOT stop the engine
    // The web-core base never stops the render loop or disposes scene/engine — every
    // unmount would leave a live rAF loop rendering a dead scene (and StrictMode /
    // repeated library mounts would stack engines on one canvas). Finish the job.
    this.engine?.stopRenderLoop();
    this.scene?.dispose();
    this.engine?.dispose();
  }

  // ---- internal flow ----

  private async onTap(mesh: Mesh, faceId: number, worldPoint: Vector3): Promise<void> {
    if (!this.planeFinderContent || !this.stages.is('view', 'framing')) return;
    // Ignore taps until the worker has finished pre-processing — otherwise the tap
    // sits on the looping latency pulse for the whole precompute. The HUD shows a
    // "Preparing model…" hint meanwhile.
    if (!this.analysisDone) return;
    const seed = this.planeFinderContent.faceToMergedTriangle(mesh, faceId);
    if (seed < 0) return;

    this.lastSeed = seed;
    this.fx.tap(worldPoint);
    this.stages.set('detecting');

    const content = this.planeFinderContent; // guard against a model swap mid-analyze
    const { id, result } = await this.session.analyze(seed, { angleToleranceDeg: this.sensitivityDeg });
    if (id !== this.session.latestRequestId) return; // superseded by a newer tap
    if (content !== this.planeFinderContent) return; // model replaced while computing

    if (!result || result.region.triangles.length < MIN_REGION_TRIANGLES) {
      this.fx.reject(worldPoint);
      this.stages.set('view');
      this.events.emit('detect-rejected', undefined as never);
      return;
    }

    this.lastResult = result;
    this.density = result.density;
    this.fx.reveal(result.region.maxDepth, result.vertexDepths);
    const oriented = this.orientTowardCamera(result.basis, result.bounds);
    this.rectController.init(oriented.basis, oriented.bounds, this.planeFinderContent.bounds!.diag);
    // Engage culling with the outside fully visible, then dither-dissolve it out over
    // the fly-to so the foreground fades away as the camera arrives (not a snap).
    this.cullingActive = true;
    this.cullFade = 0;
    this.updateCull();
    this.animateCullTo(1, CULL_FADE_MS); // dissolve the foreground out
    await this.rig.flyToPlane(oriented.basis, this.rectController.rect);
    this.rig.lockForFraming();
    this.stages.set('framing');
    this.emitRegionStats();
  }

  /**
   * Re-grow the current tap with the latest sensitivity, staying in the framing
   * stage (no camera fly, no wave animation). Drives the live sensitivity slider.
   */
  private async reanalyze(): Promise<void> {
    if (this.lastSeed == null || !this.planeFinderContent || !this.stages.is('framing')) return;
    const content = this.planeFinderContent; // guard against a model swap mid-analyze
    const { id, result } = await this.session.analyze(this.lastSeed, { angleToleranceDeg: this.sensitivityDeg });
    // Drop if superseded (rapid drag), the user left framing, or the model changed.
    if (id !== this.session.latestRequestId || !this.stages.is('framing')) return;
    if (content !== this.planeFinderContent) return;
    if (!result || result.region.triangles.length < MIN_REGION_TRIANGLES) return;

    this.lastResult = result;
    this.density = result.density;
    this.fx.reveal(result.region.maxDepth, result.vertexDepths, false); // snap, no sweep
    const oriented = this.orientTowardCamera(result.basis, result.bounds);
    this.rectController.init(oriented.basis, oriented.bounds, this.planeFinderContent.bounds!.diag);
    this.emitRegionStats();
    this.updateCull();
  }

  /**
   * Detection derives the plane normal from triangle winding; on double-sided
   * scans a tap on a back-winding face hands us a normal pointing AWAY from the
   * viewer — the camera would fly BEHIND the wall and capture through geometry.
   * Orient the basis toward the camera: flip normal AND right (keeps the frame
   * right-handed, right × up = normal) and mirror the region bounds' u-axis
   * (they were computed against the unflipped right).
   */
  private orientTowardCamera(basis: PlaneBasis, bounds: RegionBounds2D): { basis: PlaneBasis; bounds: RegionBounds2D } {
    const cam = this.camera!.position;
    const d =
      basis.normal[0] * (cam.x - basis.origin[0]) +
      basis.normal[1] * (cam.y - basis.origin[1]) +
      basis.normal[2] * (cam.z - basis.origin[2]);
    if (d >= 0) return { basis, bounds };
    return {
      basis: {
        origin: basis.origin,
        normal: [-basis.normal[0], -basis.normal[1], -basis.normal[2]],
        right: [-basis.right[0], -basis.right[1], -basis.right[2]],
        up: basis.up,
      },
      bounds: {
        min: [-bounds.max[0], bounds.min[1]],
        max: [-bounds.min[0], bounds.max[1]],
      },
    };
  }

  private cancelFraming(): void {
    this.rig.unlock();
    this.fx.clear();
    this.stages.set('view');
    // Dissolve the cull back out (foreground fades in) rather than popping instantly,
    // then fully release it. The framing UI + camera unlock immediately.
    this.animateCullTo(0, CULL_FADE_MS, () => this.disengageCull());
  }

  private onRectChange(rect: RectState): void {
    this.events.emit('rect', rect);
    this.updateCull(); // clearance scales with the rect size
    this.emitRegionStats();
  }

  private emitRegionStats(): void {
    if (!this.lastResult || !this.density) return;
    const rect = this.rectController.rect;
    const worldWidth = rect.halfW * 2;
    const worldHeight = rect.halfH * 2;
    const ideal = computeIdealCaptureSize(worldWidth, worldHeight, this.density, { maxDim: this.captureMaxDim() });
    this.events.emit('region', {
      triangleCount: this.lastResult.region.triangles.length,
      worldWidth,
      worldHeight,
      texelsPerUnit: this.density.texelsPerUnit,
      coverage: this.density.coverage,
      idealWidth: ideal.width,
      idealHeight: ideal.height,
      fallback: ideal.fallback,
      truncated: this.lastResult.region.truncated,
    });
  }
}

function uniqueMaterials(meshes: Mesh[]): Material[] {
  const set = new Set<Material>();
  for (const m of meshes) if (m.material) set.add(m.material);
  return [...set];
}
